/**
 * ContactTagsEditor — the tag Autocomplete + save flow for ONE contact.
 *
 * Extracted from ContactPanel so the inbox side panel and the contact detail
 * page edit tags through exactly the same control and the same
 * `updateContact({ tags })` call — two copies would drift on suggestions,
 * trimming/dedupe rules, or the dirty-state gate.
 *
 * Deliberately chrome-free (no Card/Section wrapper): each host supplies its
 * own container.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import adminApi from '@/services/admin.api';

interface ContactTagsEditorProps {
  contactId: string;
  /** The contact's SAVED tags — the baseline the dirty check compares against. */
  tags: string[];
  /** Read-only when false: the input is disabled and no Save button appears. */
  canEdit: boolean;
  /** Fired after a successful save so the host can patch its own copy. */
  onSaved: (contactId: string, tags: string[]) => void;
  /** Live draft size, for a host that renders a count in its section header. */
  onDraftCountChange?: (count: number) => void;
  /** Which store `contactId` lives in — an Instagram thread's id is never a
   *  row in the WhatsApp Conversation collection `updateContact` writes to,
   *  so this picks the matching endpoint. Defaults to 'whatsapp' for the
   *  Contacts detail page, which only ever shows WhatsApp contacts. */
  channel?: 'whatsapp' | 'instagram';
}

const ContactTagsEditor: React.FC<ContactTagsEditorProps> = ({
  contactId,
  tags,
  canEdit,
  onSaved,
  onDraftCountChange,
  channel = 'whatsapp',
}) => {
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState<string[]>(tags);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Held in a ref so an inline arrow from the host can't turn this into a
  // render loop — the count is a notification, never an input.
  const onDraftCountChangeRef = useRef(onDraftCountChange);
  onDraftCountChangeRef.current = onDraftCountChange;

  // Keyed on the tags' CONTENT, not the array's identity: hosts routinely pass
  // a fresh `contact.tags || []` literal, and depending on identity would
  // re-seed the draft on every render — an infinite loop, since seeding sets
  // state with a new array each time.
  const tagsKey = JSON.stringify(tags);
  const tagsRef = useRef(tags);
  tagsRef.current = tags;

  useEffect(() => {
    setTagDraft(tagsRef.current);
  }, [contactId, tagsKey]);

  useEffect(() => {
    onDraftCountChangeRef.current?.(tagDraft.length);
  }, [tagDraft]);

  useEffect(() => {
    // Suggestions always come from the WhatsApp tag vocabulary, even for an
    // Instagram thread — there is no Instagram-side "distinct tags" endpoint
    // yet, and this is a non-critical autocomplete hint (any existing tag
    // still saves fine via setInstagramConversationTags above), not a
    // correctness gap. Add a getInstagramConversationTags endpoint + branch
    // here if per-channel suggestions become worth the extra round trip.
    adminApi
      .getContactTags({ suppressPermissionLock: true })
      .then((list) => setAllTags(list || []))
      .catch(() => {});
  }, []);

  const dirty = useMemo(() => {
    const a = [...tags].sort().join('|');
    const b = [...tagDraft].sort().join('|');
    return a !== b;
  }, [tags, tagDraft]);

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      const cleaned = Array.from(new Set(tagDraft.map((t) => t.trim()).filter(Boolean)));
      if (channel === 'instagram') {
        await adminApi.setInstagramConversationTags(contactId, cleaned);
      } else {
        await adminApi.updateContact(contactId, { tags: cleaned });
      }
      onSaved(contactId, cleaned);
      // A brand-new tag becomes a suggestion for every other contact immediately.
      setAllTags((prev) => Array.from(new Set([...prev, ...cleaned])).sort());
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message || 'Failed to save tags');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Autocomplete
        multiple
        freeSolo
        size="small"
        options={allTags}
        value={tagDraft}
        onChange={(_, v) => setTagDraft(v.map(String))}
        disabled={!canEdit}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...(params as any)}
            placeholder={canEdit ? 'Add a tag… (Enter to add)' : ''}
            {...(canEdit
              ? { helperText: 'Suggestions come from tags already used on other contacts.' }
              : {})}
          />
        )}
      />
      {canEdit && dirty && (
        <Stack direction="row" spacing={1} mt={1}>
          <Button size="small" variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save tags'}
          </Button>
          <Button size="small" onClick={() => setTagDraft(tags)} disabled={saving}>
            Reset
          </Button>
        </Stack>
      )}
      {error && (
        <Typography variant="caption" color="error.main" display="block" mt={0.5}>
          {error}
        </Typography>
      )}
    </>
  );
};

export default ContactTagsEditor;
